// Shapes the `weightDebug` blob from CaptureDetail into a flat model for charting.
//
// The generated client types each `X | None` field as an opaque empty interface
// (the runtime value is the real primitive), so `num`/`bool` coerce those back to
// usable types. Required fields (weightGrams, timestamp, rawValue) are already typed.
import type { WeightDebug } from '../api'
import { Devices, DeviceEventType } from '../api'

export interface CellSample {
  weight: number // weight_grams contribution
  raw: number // raw_value (ADC counts)
}

export interface WeightReadingPoint {
  t: number // timestamp, seconds
  weight: number // weight_grams (rebalanced, reported basis)
  smoothed: number | null
  raw: number | null
  outlier: boolean
  used: boolean // used_for_reported_weight
  cells: Record<string, CellSample>
}

export interface CellCalibration {
  ch: string
  slope: number | null
  xIntercept: number | null
  r2: number | null
  sign: number | null // +1 / -1 polarity, 0 = disabled
  enabled: boolean
  expectedFraction: number | null
}

export interface WeightTrajectory {
  readings: WeightReadingPoint[]
  stable: boolean | null
  stableStart: number | null
  stableEnd: number | null
  stableStddev: number | null
  reportedWeight: number | null
  cellKeys: string[]
  cellCalibration: CellCalibration[]
}

const num = (v: unknown): number | null => (typeof v === 'number' ? v : null)
const bool = (v: unknown): boolean => v === true

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export function medianWeightInWindow(
  readings: WeightReadingPoint[],
  startT: number,
  endT: number,
): number | null {
  return median(readings.filter(r => r.t >= startT && r.t <= endT).map(r => r.weight))
}

export function parseWeightTrajectory(weightDebug: WeightDebug | null | undefined): WeightTrajectory | null {
  const rawReadings = weightDebug?.scaleReadings
  if (!rawReadings || rawReadings.length === 0) return null

  const cellKeySet = new Set<string>()
  const readings: WeightReadingPoint[] = rawReadings.map(r => {
    const channelReadings = (r.channelReadings ?? {}) as Record<
      string,
      { weightGrams: number; rawValue: number }
    >
    const cells: Record<string, CellSample> = {}
    for (const [ch, cr] of Object.entries(channelReadings)) {
      cellKeySet.add(ch)
      cells[ch] = { weight: num(cr.weightGrams) ?? 0, raw: num(cr.rawValue) ?? 0 }
    }
    return {
      t: r.timestamp,
      weight: r.weightGrams,
      smoothed: num(r.smoothedWeightGrams),
      raw: num(r.rawWeightGrams),
      outlier: bool(r.outlier),
      used: bool(r.usedForReportedWeight),
      cells,
    }
  })
  readings.sort((a, b) => a.t - b.t)
  const cellKeys = Array.from(cellKeySet).sort((a, b) => a.localeCompare(b))

  const usedWeights = readings.filter(r => r.used).map(r => r.weight)
  const reportedWeight =
    usedWeights.length > 0
      ? median(usedWeights)
      : median(readings.filter(r => !r.outlier).map(r => r.weight))

  const cal = weightDebug.calibration
  const signs = (cal?.signs ?? {}) as Record<string, number>
  const calItems = (cal?.calibrations ?? []) as Array<{
    slope?: unknown
    xIntercept?: unknown
    r2?: unknown
  }>
  const expected = (cal?.expectedFractions ?? {}) as Record<string, number>
  const cellCalibration: CellCalibration[] = cal
    ? cellKeys.map(ch => {
        const item = calItems[Number(ch)] ?? {}
        const sign = num(signs[ch])
        return {
          ch,
          slope: num(item.slope),
          xIntercept: num(item.xIntercept),
          r2: num(item.r2),
          sign,
          enabled: sign != null ? sign !== 0 : true,
          expectedFraction: num(expected[ch]),
        }
      })
    : []

  return {
    readings,
    stable: weightDebug.weightStable == null ? null : bool(weightDebug.weightStable),
    stableStart: num(weightDebug.weightStableWindowStartTs),
    stableEnd: num(weightDebug.weightStableWindowEndTs),
    stableStddev: num(weightDebug.weightStableWindowStddevGrams),
    reportedWeight,
    cellKeys,
    cellCalibration,
  }
}

// ---- Device-event context around a capture ----
//
// The capture's own scale_readings only cover its ~9 s weigh window. Outside it
// the device logs one weight_read event per event-poller tick (~5 s), plus tare,
// calibration_saved and capture_triggered events. These feed the chart's wider
// windows and the tare markers.

export type WindowUnit = 's' | 'm' | 'h'
export const UNIT_SECONDS: Record<WindowUnit, number> = { s: 1, m: 60, h: 3600 }

// View window around the capture: `before` ahead of its start to `after` past its
// end. 0 / 0 is the capture's own readings only.
export interface WindowSpec {
  before: number
  beforeUnit: WindowUnit
  after: number
  afterUnit: WindowUnit
}

export const DEFAULT_WINDOW: WindowSpec = { before: 0, beforeUnit: 's', after: 0, afterUnit: 's' }

// Per side. 12 h of 5 s reads is ~8.6k rows each way, well under the
// endpoint's 50k unsampled cap.
export const MAX_WINDOW_PAD_S = 12 * 3600

export interface LoggedReading {
  t: number
  weight: number
}

export interface TareMark {
  t: number
  grams: number | null // tare_grams: the offset the tare removed
}

export interface CaptureMark {
  t: number
  captureId: string
}

export interface WeightContext {
  readings: LoggedReading[] // weight_read events in the window (empty for the capture window)
  tares: TareMark[] // over TARE_LOOKBACK before to the window end, so "last tare" is found
  calibrations: number[]
  captures: CaptureMark[] // capture_triggered events in the window
}

// How far back to look for the last tare before a capture. Tares are minutes
// apart at best (median capture is ~13 min from its nearest one).
export const TARE_LOOKBACK_S = 12 * 3600

const toS = (d: Date) => d.getTime() / 1000

export async function fetchWeightContext(
  hostname: string,
  windowStart: number,
  windowEnd: number,
  captureStart: number,
  withReadings: boolean,
): Promise<WeightContext> {
  const list = (types: DeviceEventType[], from: number, to: number) =>
    Devices.listDeviceEvents({
      listDeviceEventsRequest: {
        hostname,
        fromTimestamp: new Date(from * 1000),
        toTimestamp: new Date(to * 1000),
        eventTypes: types,
        maxPerType: 0, // ±1 h of 5 s reads is ~1.5k rows; keep them all
      },
    }).then(r => r.events)

  const [marks, inWindow] = await Promise.all([
    list(
      [DeviceEventType.Tare, DeviceEventType.CalibrationSaved],
      Math.min(windowStart, captureStart - TARE_LOOKBACK_S),
      windowEnd,
    ),
    list(
      withReadings
        ? [DeviceEventType.WeightRead, DeviceEventType.CaptureTriggered]
        : [DeviceEventType.CaptureTriggered],
      windowStart,
      windowEnd,
    ),
  ])

  const ctx: WeightContext = { readings: [], tares: [], calibrations: [], captures: [] }
  for (const ev of marks) {
    const p = ev.payload as unknown as Record<string, unknown>
    if (ev.eventType === DeviceEventType.Tare) ctx.tares.push({ t: toS(ev.eventTimestamp), grams: num(p.tareGrams) })
    else ctx.calibrations.push(toS(ev.eventTimestamp))
  }
  for (const ev of inWindow) {
    const p = ev.payload as unknown as Record<string, unknown>
    if (ev.eventType === DeviceEventType.WeightRead) {
      const w = num(p.weightGrams)
      if (w != null) ctx.readings.push({ t: toS(ev.eventTimestamp), weight: w })
    } else if (typeof p.captureId === 'string') {
      ctx.captures.push({ t: toS(ev.eventTimestamp), captureId: p.captureId })
    }
  }
  ctx.readings.sort((a, b) => a.t - b.t)
  ctx.tares.sort((a, b) => a.t - b.t)
  return ctx
}

// ---- Pound axis over gram data ----

export const LB_PER_GRAM = 1 / 453.592

const LB_STEPS = [0.01, 0.02, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100]

/** Ticks at round pound values, in grams (the charts' data unit), plus a
 *  matching label formatter. Recharts' own ticks land on round grams. */
export function poundTicks(gramValues: number[], target = 6): { ticks: number[]; fmt: (g: number) => string } {
  const lbs = gramValues.filter(Number.isFinite).map(g => g * LB_PER_GRAM)
  if (lbs.length === 0) return { ticks: [], fmt: g => (g * LB_PER_GRAM).toFixed(1) }
  let lo = Math.min(...lbs)
  let hi = Math.max(...lbs)
  if (hi - lo < 0.02) { lo -= 0.05; hi += 0.05 }
  const step = LB_STEPS.find(s => (hi - lo) / s <= target) ?? LB_STEPS[LB_STEPS.length - 1]
  const first = Math.floor(lo / step)
  const last = Math.ceil(hi / step)
  const ticks: number[] = []
  for (let k = first; k <= last; k++) ticks.push((k * step) / LB_PER_GRAM)
  const digits = step >= 1 ? 0 : step >= 0.1 ? 1 : 2
  return { ticks, fmt: g => (g * LB_PER_GRAM).toFixed(digits) }
}
